"use client";

import { useState } from "react";
import dynamic from "next/dynamic";
import Link from "next/link";
import { toast } from "sonner";
import { Plus, Expand, SlidersHorizontal, Loader2 } from "lucide-react";
import { formatPrice } from "@/lib/format-price";
import { productPath } from "@/lib/product-url";
import { useAddToCart } from "@/hooks/useAddToCart";
import { useIsMobile } from "@/hooks/useIsMobile";
import {
  resolveVariantPrice,
  type VariantOption,
} from "@/components/products/VariantSelector";
import type { Product } from "@/stores/product-store";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

// The product drawer pulls in ProductDetail + VariantSelector + the full
// image gallery — lazy-load it so it doesn't weigh down the PDP/checkout
// bundle until a customer actually opens a variant product's options.
const ProductDetailDrawer = dynamic(
  () =>
    import("@/components/products/detail/ProductDetailDrawer").then((m) => ({
      default: m.ProductDetailDrawer,
    })),
  { ssr: false }
);

/** Minimal product shape the strip needs (subset of the public product). */
export interface CrossSellItem {
  id: string;
  slug?: string | null;
  title: string;
  pricePence: number | null;
  category: string;
  imageKey: string;
  imageUrl?: string;
  imageUrlRaw?: string;
  isAddon?: number;
  /** True when the product has selectable variants (Male/Female, size, …).
   *  Such items can't be quick-added — tapping them opens the product in a
   *  dialog/sheet so the customer picks the variant without leaving checkout. */
  hasVariants?: boolean;
}

/** Best available image URL for an item (raw R2 original → proxied → fallback). */
function imageSrc(item: CrossSellItem): string {
  return (
    item.imageUrlRaw ||
    item.imageUrl ||
    `/api/gallery/image?key=${encodeURIComponent(item.imageKey)}`
  );
}

/**
 * "Add a little extra" cross-sell strip (WM3). Surfaces giftable add-ons +
 * accessories on the PDP + checkout. Each tile links to the product
 * (crawlable), has a quick Add-to-cart that doesn't navigate away, and a zoom
 * control that opens the image full-size in a lightbox. Variant products open
 * a product dialog/sheet IN PLACE (no redirect to /p/[id]) so the customer can
 * pick their option and add to cart without bouncing around. Renders nothing
 * when there are no items.
 */
export function CrossSellStrip({
  items,
  heading = "Add a little extra",
  className = "",
}: {
  items: CrossSellItem[];
  heading?: string;
  className?: string;
}) {
  // Cart write + GA4 add_to_cart in one call — see hooks/useAddToCart.
  const addToCart = useAddToCart();
  const isMobile = useIsMobile();
  const [zoomItem, setZoomItem] = useState<CrossSellItem | null>(null);
  // Variant-picker dialog state. We fetch the full product (with variants) on
  // demand and host it in the shared ProductDetailDrawer — the SAME UI the PDP
  // uses, so variant capture into the cart is identical + already tested.
  const [variantProduct, setVariantProduct] = useState<Product | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [loadingId, setLoadingId] = useState<string | null>(null);
  if (!items || items.length === 0) return null;

  const add = (item: CrossSellItem) => {
    if (item.pricePence == null) return;
    // Variant products are never quick-added here — they open the picker
    // dialog to choose (Male/Female, size, …). Guard defensively so a stray
    // call can't drop a variant-less line into the cart.
    if (item.hasVariants) return;
    addToCart({
      productId: item.id,
      quantity: 1,
      priceSnapshot: item.pricePence,
      title: item.title,
      imageKey: item.imageKey,
      imageUrlRaw: item.imageUrlRaw,
      category: item.category,
      isAddon: Boolean(item.isAddon),
    });
    toast.success("Added to cart");
  };

  /** Fetch the full product (with variants) and open the picker dialog. */
  const openVariantPicker = async (item: CrossSellItem) => {
    if (loadingId) return;
    setLoadingId(item.id);
    try {
      const res = await fetch(`/api/products/${encodeURIComponent(item.id)}`);
      if (!res.ok) throw new Error("load failed");
      const product = (await res.json()) as Product;
      setVariantProduct(product);
      setDrawerOpen(true);
    } catch {
      toast.error("Couldn't load options — please try again.");
    } finally {
      setLoadingId(null);
    }
  };

  /** Add-to-cart from the picker dialog — mirrors the PDP/products-page
   *  mapping so the order records the chosen variant + its price. */
  const handleDrawerAddToCart = (
    p: Product,
    quantity: number,
    variant?: VariantOption | null
  ) => {
    const price = variant ? resolveVariantPrice(variant, p.pricePence) : p.pricePence;
    if (price == null) return;
    addToCart({
      productId: p.id,
      quantity,
      priceSnapshot: price,
      title: p.title,
      imageKey: p.imageKey,
      imageUrlRaw: p.imageUrlRaw,
      category: p.category,
      isAddon: !!p.isAddon,
      ...(variant ? { variantId: variant.id, variantLabel: variant.label } : {}),
    });
    toast.success("Added to cart");
    setDrawerOpen(false);
  };

  // Keep the drawer open when a click lands inside the product image lightbox
  // (it portals outside the dialog) — matches ProductsClient's behaviour.
  const onPointerDownOutside = (e: {
    target: EventTarget | null;
    preventDefault: () => void;
  }) => {
    const el = e.target as HTMLElement | null;
    if (el?.closest?.("[data-product-lightbox]")) e.preventDefault();
  };

  return (
    <section aria-label={heading} className={className}>
      <h2 className="heading-card">{heading}</h2>
      {/* Smaller tiles than the main grid (these are impulse extras, not the
          hero products): 3-up on mobile, up to 5-up on desktop. */}
      <ul className="mt-4 grid grid-cols-3 gap-2.5 sm:grid-cols-4 lg:grid-cols-5">
        {items.map((item) => (
          <li key={item.id} className="overflow-hidden rounded-lg border border-border/60 bg-card">
            <div className="group relative aspect-square w-full overflow-hidden bg-muted">
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={imageSrc(item)}
                alt={item.title}
                loading="lazy"
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
              />
              {/* Full-image link overlay sits UNDER the zoom button. It stays a
                  crawlable anchor to the product page, but for VARIANT items a
                  real click opens the picker dialog in place (no redirect). */}
              <Link
                href={productPath(item)}
                onClick={(e) => {
                  if (item.hasVariants) {
                    e.preventDefault();
                    void openVariantPicker(item);
                  }
                }}
                className="absolute inset-0 z-0 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
              >
                <span className="sr-only">
                  {item.hasVariants ? `Choose options for ${item.title}` : `View ${item.title}`}
                </span>
              </Link>
              <button
                type="button"
                onClick={() => setZoomItem(item)}
                aria-label={`View larger image of ${item.title}`}
                className="absolute right-1.5 top-1.5 z-10 flex min-h-11 min-w-11 size-11 items-center justify-center rounded-full bg-card/85 text-foreground shadow-sm backdrop-blur-sm transition-colors hover:bg-card focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring sm:size-8 sm:min-h-0 sm:min-w-0"
              >
                <Expand className="h-3.5 w-3.5" />
              </button>
            </div>
            {/* Compact footer: title on its own line, then price + add button,
                so the 44px add target still fits comfortably on small tiles. */}
            <div className="px-2 py-2">
              <Link
                href={productPath(item)}
                onClick={(e) => {
                  if (item.hasVariants) {
                    e.preventDefault();
                    void openVariantPicker(item);
                  }
                }}
                className="block truncate text-xs font-medium text-foreground hover:text-primary"
                title={item.title}
              >
                {item.title}
              </Link>
              <div className="mt-1 flex items-center justify-between gap-1.5">
                <p className="text-xs font-semibold tabular-nums text-muted-foreground">
                  {item.pricePence != null ? formatPrice(item.pricePence) : "—"}
                </p>
                {item.hasVariants ? (
                  <button
                    type="button"
                    onClick={() => void openVariantPicker(item)}
                    disabled={loadingId === item.id}
                    aria-label={`Choose options for ${item.title}`}
                    title="Choose options"
                    className="inline-flex min-h-11 min-w-11 size-11 shrink-0 items-center justify-center rounded-full border border-primary text-primary transition-colors hover:bg-primary/10 disabled:opacity-60 sm:size-8 sm:min-h-0 sm:min-w-0"
                  >
                    {loadingId === item.id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <SlidersHorizontal className="h-4 w-4" />
                    )}
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => add(item)}
                    aria-label={`Add ${item.title} to cart`}
                    className="inline-flex min-h-11 min-w-11 size-11 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground transition-colors hover:bg-primary/90 sm:size-8 sm:min-h-0 sm:min-w-0"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
          </li>
        ))}
      </ul>

      {/* Image lightbox — opens the selected tile's image full-size, with a
          quick add-to-cart so the customer can act without leaving the page. */}
      <Dialog open={!!zoomItem} onOpenChange={(open) => !open && setZoomItem(null)}>
        <DialogContent className="overflow-hidden p-0 sm:max-w-2xl">
          {zoomItem && (
            <>
              <DialogTitle className="sr-only">{zoomItem.title}</DialogTitle>
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={imageSrc(zoomItem)}
                alt={zoomItem.title}
                className="max-h-[78vh] w-full bg-muted object-contain"
              />
              <div className="flex items-center justify-between gap-3 px-4 pb-4 pt-1">
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-foreground" title={zoomItem.title}>
                    {zoomItem.title}
                  </p>
                  <p className="text-sm font-semibold tabular-nums text-muted-foreground">
                    {zoomItem.pricePence != null ? formatPrice(zoomItem.pricePence) : "—"}
                  </p>
                </div>
                {zoomItem.hasVariants ? (
                  <Button
                    type="button"
                    className="shrink-0"
                    onClick={() => {
                      const it = zoomItem;
                      setZoomItem(null);
                      if (it) void openVariantPicker(it);
                    }}
                  >
                    <SlidersHorizontal className="mr-1.5 h-4 w-4" />
                    Choose options
                  </Button>
                ) : (
                  zoomItem.pricePence != null && (
                    <Button
                      type="button"
                      onClick={() => {
                        add(zoomItem);
                        setZoomItem(null);
                      }}
                      className="shrink-0"
                    >
                      <Plus className="mr-1.5 h-4 w-4" />
                      Add to cart
                    </Button>
                  )
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Variant picker — the full product in a dialog (desktop) / bottom sheet
          (mobile), so variant items are chosen + added without leaving the
          page. Lazily mounted once a variant product has been loaded. */}
      {variantProduct && (
        <ProductDetailDrawer
          product={variantProduct}
          open={drawerOpen}
          onOpenChange={(o) => {
            setDrawerOpen(o);
            if (!o) setVariantProduct(null);
          }}
          onClose={() => setDrawerOpen(false)}
          onPointerDownOutside={onPointerDownOutside}
          onAddToCart={handleDrawerAddToCart}
          useMobileLayout={isMobile}
        />
      )}
    </section>
  );
}
